/* Video grid — behavior port of live's legacy-prefixed custom element
 * (assets/custom.js, webpack module 707) plus the deliberate performance
 * rebuild of its video delivery.
 *
 * Ported 1:1 from live:
 * - Category filter: click strips `active` from every pill, adds it to the
 *   clicked one, then shows at most FOUR matching items (DOM order) via
 *   inline style.display and hides the rest. Shown items get a 300 ms
 *   fade-in class. Matching splits data-category on spaces, exact string
 *   match. No scroll reset on switch — live keeps the carousel position.
 * - The first pill's category is auto-applied on connect (live reads
 *   categoryButtons[0], not `.active`).
 * - With no category pills, every item is shown.
 *
 * Deliberately NOT ported (the 70 MB problem, GAUNTLET §2): live's videos
 * carry the autoplay attribute, so all of them — including the six the
 * filter hides — download and decode at page load. Here the section renders
 * preload="none" + poster (the video's own first frame, so first paint is
 * pixel-identical), and this element starts playback only when a video's
 * item first intersects the viewport. Once started, a video keeps playing
 * exactly like live's (muted, looping, never paused by this element).
 *
 * Enhancement only: without JS the grid shows the server-rendered first
 * category with each video's first frame; every link keeps working.
 *
 * Items the server renders hidden carry their poster as data-poster (a
 * poster attribute downloads even under display:none); showPoster() turns it
 * into the real poster the first time the item is shown.
 */
class VideoGrid extends HTMLElement {
  connectedCallback() {
    this.categoryButtons = this.querySelectorAll('.video-grid__category-btn');
    this.videoItems = this.querySelectorAll('.video-grid__item-wrapper');

    this.playbackObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const video = entry.target.querySelector('video');
          this.playbackObserver.unobserve(entry.target);
          if (!video) return;
          const started = video.play();
          if (started && typeof started.catch === 'function') {
            started.catch(() => {});
          }
        });
      },
      { rootMargin: '0px 0px 200px 0px' }
    );
    this.videoItems.forEach((item) => this.playbackObserver.observe(item));

    if (this.categoryButtons.length > 0) {
      this.categoryButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          this.categoryButtons.forEach((candidate) => candidate.classList.remove('active'));
          button.classList.add('active');
          this.filterVideos(button.dataset.category);
        });
      });
      const first = this.categoryButtons[0];
      if (first) this.filterVideos(first.dataset.category);
    } else {
      this.videoItems.forEach((item) => {
        item.style.display = '';
        this.showPoster(item);
      });
    }
  }

  showPoster(item) {
    const video = item.querySelector('video[data-poster]');
    if (!video) return;
    video.poster = video.dataset.poster;
    video.removeAttribute('data-poster');
  }

  disconnectedCallback() {
    if (this.playbackObserver) this.playbackObserver.disconnect();
  }

  filterVideos(category) {
    let count = 0;
    this.videoItems.forEach((item) => {
      const raw = item.dataset.category || '';
      const list = raw.trim() !== '' ? raw.split(' ').filter((entry) => entry.trim() !== '') : [];
      if (list.length > 0 && list.includes(category) && count < 4) {
        item.style.display = '';
        this.showPoster(item);
        item.classList.add('video-grid__item--visible');
        setTimeout(() => {
          item.classList.remove('video-grid__item--visible');
        }, 300);
        count += 1;
      } else {
        item.style.display = 'none';
      }
    });
  }
}

if (!customElements.get('video-grid')) {
  customElements.define('video-grid', VideoGrid);
}
